import { CloseOutlined } from "@ant-design/icons";
import { FC, useEffect, useEffectEvent, useState } from "react";
import {
  CHAT_USED_KEY, HINT_DELAY_MS, HINT_SHOWN_COOKIE, HINT_SHOWN_MS, HINT_TEXT, hasCookie, hintWanted,
} from "./xbotHintRule";

// What the hint remembers lives in this browser only: a browser that keeps nothing simply shows the hint again.

function chatUsed(): boolean {
  try {
    return localStorage.getItem(CHAT_USED_KEY) === "1";
  } catch {
    return false;
  }
}

function keepChatUsed(): void {
  try {
    localStorage.setItem(CHAT_USED_KEY, "1");
  } catch {
    // not kept: the hint comes again the next time the browser opens
  }
}

function shownSinceBrowserOpened(): boolean {
  return hasCookie(document.cookie, HINT_SHOWN_COOKIE);
}

function keepShownSinceBrowserOpened(): void {
  document.cookie = `${HINT_SHOWN_COOKIE}=1; path=/; SameSite=Strict`;
}

type HintPhase = "waiting" | "shown" | "gone";

/**
 * A small bubble over the chat icon saying what it is, a moment after a page opens: once each time the browser
 * opens, until the chat has been opened in this browser. It goes on a press (which opens the chat), on its cross, by
 * itself after a while, or when the chat is opened.
 */
export const XbotHint: FC<{ chatOpen: boolean; onOpenChat: () => void }> = ({ chatOpen, onOpenChat }) => {
  const [phase, setPhase] = useState<HintPhase>(() =>
    (hintWanted(chatUsed(), shownSinceBrowserOpened(), chatOpen) ? "waiting" : "gone"));

  // the chat opened, by its icon or by the hint: the person knows it now, the hint does not come again here
  const onChatOpened = useEffectEvent(() => {
    keepChatUsed();
    if (phase !== "gone") {
      setPhase("gone");
    }
  });
  useEffect(() => {
    if (chatOpen) {
      onChatOpened();
    }
  }, [chatOpen]);

  useEffect(() => {
    if (phase === "waiting") {
      const timer = window.setTimeout(() => {
        // a page opened in another tab meanwhile may have shown it already
        if (shownSinceBrowserOpened()) {
          setPhase("gone");
          return;
        }
        keepShownSinceBrowserOpened();
        setPhase("shown");
      }, HINT_DELAY_MS);
      return () => window.clearTimeout(timer);
    }
    if (phase === "shown") {
      const timer = window.setTimeout(() => setPhase("gone"), HINT_SHOWN_MS);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [phase]);

  if (phase !== "shown") {
    return null;
  }
  return <XbotHintView
    onOpen={() => {
      setPhase("gone");
      onOpenChat();
    }}
    onClose={() => setPhase("gone")}
  />;
};

/** The bubble itself: a press on its words opens the chat, its cross closes it. */
export const XbotHintView: FC<{ onOpen: () => void; onClose: () => void }> = ({ onOpen, onClose }) =>
  <div className="xm-chat-hint" role="status">
    <button type="button" className="xm-chat-hint-text" onClick={onOpen}>{HINT_TEXT}</button>
    <button type="button" className="xm-chat-hint-close" aria-label="Close the hint" onClick={onClose}>
      <CloseOutlined aria-hidden />
    </button>
  </div>;
